import { p as proxyCustomElement, H, c as createEvent, h } from './p-BJEvNVLM.js';
import { i as isNodeEnvironment, b as bem } from './p-g1JgJ7wn.js';
import { r as resolveAppearance } from './p-CkrRd9Ud.js';

const IMAGE_STATE_LOAD_SUCCESS = 'success';
const IMAGE_STATE_LOAD_ERROR = 'error';
const IMAGE_STATE_LOADING = 'loading';

const cosmosImageCss = ":host{display:block;height:100%}.cosmos-image{display:flex;height:100%;isolation:isolate;min-height:inherit;overflow:hidden}.cosmos-image--radius-none{border-radius:var(--cosmos-image-border-radius, 0)}.cosmos-image--radius-small{border-radius:var(--cosmos-image-border-radius, 2px)}.cosmos-image--radius-medium{border-radius:var(--cosmos-image-border-radius, 6px)}.cosmos-image--radius-large{border-radius:var(--cosmos-image-border-radius, 8px)}.cosmos-image--radius-full{border-radius:var(--cosmos-image-border-radius, 9999px)}.cosmos-image--fallback.cosmos-image--appearance-light{background-color:rgba(0, 15, 30, 0.05)}.cosmos-image--fallback.cosmos-image--appearance-dark{background-color:rgba(255, 255, 255, 0.1)}.cosmos-image--image-state-loading,.cosmos-image--fit-contain{background-color:transparent}.cosmos-image__container{display:grid;grid-template-areas:\"image\";height:100%;width:100%}.cosmos-image--has-preview:not(.cosmos-image--has-image) .cosmos-image__container{display:flex}.cosmos-image--fit-contain .cosmos-image__container{place-content:center}.cosmos-image__preview-container,.cosmos-image__image{grid-area:image;max-height:100%;min-height:0}.cosmos-image--fit-cover .cosmos-image__preview-container,.cosmos-image--fit-cover .cosmos-image__image{}@supports (hanging-punctuation: first) and (font: -apple-system-body) and (-webkit-appearance: none){.cosmos-image--fit-cover .cosmos-image__preview-container,.cosmos-image--fit-cover .cosmos-image__image{width:100%}}.cosmos-image--fit-contain.cosmos-image--radius-none .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-none .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 0)}.cosmos-image--fit-contain.cosmos-image--radius-small .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-small .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 2px)}.cosmos-image--fit-contain.cosmos-image--radius-medium .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-medium .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 6px)}.cosmos-image--fit-contain.cosmos-image--radius-large .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-large .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 8px)}.cosmos-image--fit-contain.cosmos-image--radius-full .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-full .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 9999px)}.cosmos-image__preview-image,.cosmos-image__image{object-fit:cover}.cosmos-image__preview-container{display:flex;overflow:hidden;position:relative}.cosmos-image--fit-cover .cosmos-image__preview-container{width:100%}.cosmos-image__preview-image{filter:blur(5px);height:calc(100% + 5px * 2);inset:0;position:absolute;transform:translate(-5px, -5px);width:calc(100% + 5px * 2)}.cosmos-image__preview-dummy{max-width:100%}.cosmos-image--has-preview:not(.cosmos-image--has-image) .cosmos-image__preview-dummy{width:100%}.cosmos-image__image{max-width:100%;transition:opacity 0.2s cubic-bezier(0, 0.32, 0.32, 1);z-index:1}.cosmos-image--image-state-loading .cosmos-image__image{opacity:0}.cosmos-image--image-state-success .cosmos-image__image{opacity:1;width:100%}.cosmos-image--fit-cover .cosmos-image__image{height:100%}";

const generateClassNames = bem('cosmos-image');
const CosmosImage = /*@__PURE__*/ proxyCustomElement(class CosmosImage extends H {
    handleAppearanceChange(appearance) {
        this.computedAppearance = appearance;
    }
    handleImageChange(newValue, oldValue) {
        if (newValue === oldValue) {
            return;
        }
        this.imageState = IMAGE_STATE_LOADING;
    }
    handleObserveResizeChange(newValue) {
        if (this.containerResizeObserver) {
            this.containerResizeObserver.unobserve(this.container);
            if (newValue) {
                this.containerResizeObserver.observe(this.container);
            }
        }
    }
    handleContainerResize(entries) {
        entries.forEach((entry) => {
            if (entry.target === this.container) {
                const { height, width } = entry.contentRect;
                this.resize.emit({
                    height,
                    width,
                });
            }
        });
    }
    preloadImage() {
        if (typeof Image !== 'undefined') {
            const image = new Image();
            image.src = this.image;
            // check if image is already loaded
            if (image.complete) {
                this.imageState = IMAGE_STATE_LOAD_SUCCESS;
            }
            else {
                this.imageState = IMAGE_STATE_LOADING;
            }
        }
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.resize = createEvent(this, "resize", 5);
        /**
         * Specifies how the image should be resized to fit the component's boundaries while preserving its aspect ratio.
         * - 'cover': Scales and crops the image to fill the container.
         * - 'contain': Scales the image to fit within the container.
         */
        this.fit = 'cover';
        /**
         * The accessible, alternative text of the featured image.
         */
        this.imageAlt = '';
        /**
         * Sets the border-radius for the featured image, preview image, and fallback image.
         */
        this.radius = 'none';
        this.computedAppearance = 'light';
        this.handleImageLoad = () => {
            this.imageState = IMAGE_STATE_LOAD_SUCCESS;
        };
        this.handleImageError = () => {
            this.imageState = IMAGE_STATE_LOAD_ERROR;
        };
        this.handlePreviewError = () => {
            this.previewState = IMAGE_STATE_LOAD_ERROR;
        };
        this.shouldDisplayFallback = () => {
            return !(this.shouldDisplayImage() || this.shouldDisplayPreview());
        };
        this.shouldDisplayImage = () => {
            return !!this.image && this.imageState !== IMAGE_STATE_LOAD_ERROR;
        };
        this.shouldDisplayPreview = () => {
            return !!this.imagePreview && this.previewState !== IMAGE_STATE_LOAD_ERROR;
        };
        if (isNodeEnvironment()) {
            return;
        }
        this.containerResizeObserver = new ResizeObserver((entries) => this.handleContainerResize(entries));
    }
    componentWillLoad() {
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'dark', light: 'light' },
            callback: this.handleAppearanceChange.bind(this),
        });
        if (!this.srcset && this.image) {
            this.preloadImage();
        }
    }
    componentDidLoad() {
        if (this.observeResize) {
            this.handleObserveResizeChange(this.observeResize);
        }
    }
    render() {
        return (h("div", { key: 'b634e2b180ea85d277e92494361f30f7e730f253', class: generateClassNames({
                'appearance': this.computedAppearance,
                'fallback': this.shouldDisplayFallback(),
                'fit': this.fit,
                'has-image': !!this.image,
                'has-preview': !!this.imagePreview,
                'image-state': this.imageState,
                'radius': this.radius,
            }), part: "base", ref: (element) => (this.container = element) }, !this.shouldDisplayFallback() && (h("div", { key: '783507b57daa9692035b2b82d520c1ac72d6ff13', class: generateClassNames('container') }, this.shouldDisplayPreview() && (h("div", { key: 'a2575537f6c50978f36f3f895225f8b210dbaa35', class: generateClassNames('preview-container') }, h("img", { key: '1e55034b5f17f75d5c606de28abbba1f42861d69', alt: "", class: generateClassNames('preview-image'), onError: this.handlePreviewError, src: this.imagePreview }), h("img", { key: '5a828d355b352b067032b3da54046051538798dd', alt: "", class: generateClassNames('preview-dummy'), src: this.imagePreview }))), this.shouldDisplayImage() && (h("img", { key: '528798429b4ef190d3bb4bc07ae43d3cf0d050e9', alt: this.imageAlt, class: generateClassNames('image'), loading: this.loading, onError: this.handleImageError, onLoad: this.handleImageLoad, part: "image", sizes: this.sizes, src: this.image, srcSet: this.srcset }))))));
    }
    get element() { return this; }
    static get watchers() { return {
        "appearance": ["handleAppearanceChange"],
        "image": ["handleImageChange"],
        "observeResize": ["handleObserveResizeChange"]
    }; }
    static get style() { return cosmosImageCss; }
}, [1, "cosmos-image", {
        "appearance": [513],
        "fit": [513],
        "image": [513],
        "imageAlt": [513, "image-alt"],
        "imagePreview": [513, "image-preview"],
        "loading": [513],
        "observeResize": [516, "observe-resize"],
        "radius": [513],
        "sizes": [513],
        "srcset": [513],
        "computedAppearance": [32],
        "imageState": [32],
        "previewState": [32]
    }, undefined, {
        "appearance": ["handleAppearanceChange"],
        "image": ["handleImageChange"],
        "observeResize": ["handleObserveResizeChange"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-image"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-image":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosImage);
            }
            break;
    } });
}

export { CosmosImage as C, defineCustomElement as d };
